"use client";

import cnBind from "classnames/bind";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { FormCallBack, type FormCallBackCopy } from "@/components/_Forms/FormCallBack";
import back from "@/shared/assests/TMA/background.svg";
import mess from "@/shared/assests/TMA/Message.png";
import qr from "@/shared/assests/TMA/qr-tezam.png";
import { Routes } from "@/shared/constants/Routing";
import { CustomImage } from "@/shared/ui/CustomImage";

import styles from "./Form.module.scss";

const cx = cnBind.bind(styles);

const TELEGRAM_CHANNEL_URL = Routes.TELEGRAM_CHANNEL;

type FormProps = {
    variant?: "default" | "dark";
    copy?: FormCallBackCopy;
    showTelegram?: boolean;
};

export const TelegramChannelBanner = () => {
    const { t } = useTranslation("common");

    return (
        <div className={cx("images", "container")}>
            <div
                className={cx("telegramBanner")}
                style={{ backgroundImage: `url(${back})` }}
            >
                <div className={cx("brandCol")}>
                    <CustomImage
                        className={cx("messageArt")}
                        src={mess}
                        alt={t("home.telegramBanner.alt")}
                        width={336}
                        height={87}
                    />
                </div>

                <div className={cx("actionsCol")}>
                    <a
                        className={cx("channelCard")}
                        href={TELEGRAM_CHANNEL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <span className={cx("channelIcon")} aria-hidden="true">
                            <svg width="22" height="22" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M14.4735 3.62544L12.4339 13.2444C12.2798 13.9232 11.8787 14.0921 11.3085 13.7726L8.20054 11.4824L6.70106 12.9248C6.53499 13.0909 6.39646 13.2294 6.0764 13.2294L6.2999 10.0645L12.0597 4.85985C12.3103 4.63681 12.0052 4.51275 11.6706 4.73626L4.54992 9.22012L1.4844 8.26039C0.817708 8.05234 0.805642 7.59369 1.62343 7.27363L13.6137 2.65413C14.1689 2.44605 14.6546 2.77723 14.4735 3.62544Z"
                                    fill="currentColor"
                                />
                            </svg>
                        </span>
                        <span className={cx("channelCopy")}>
                            <span className={cx("eyebrow")}>{t("home.telegramBanner.eyebrow")}</span>
                            <span className={cx("handle")}>@tezamchanel</span>
                            <span className={cx("ctaLabel")}>{t("home.telegramBanner.cta")}</span>
                        </span>
                        <ChevronRight className={cx("chevron")} aria-hidden="true" strokeWidth={2.25} />
                    </a>

                    <a
                        className={cx("qrCard")}
                        href={TELEGRAM_CHANNEL_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t("home.telegramBanner.qrAria")}
                    >
                        <span className={cx("qrFrame")}>
                            <CustomImage className={cx("qr")} height={88} width={88} src={qr} alt="" aria-hidden="true" />
                        </span>
                        <span className={cx("qrLabel")}>{t("home.telegramBanner.qrLabel")}</span>
                    </a>
                </div>
            </div>
        </div>
    );
};

export const Form = ({ variant = "default", copy, showTelegram = true }: FormProps) => {
    return (
        <div className={cx("form")} id="form">
            <div className={cx("wrapper", "container")}>
                <FormCallBack variant={variant} copy={copy} />
            </div>
            {showTelegram ? <TelegramChannelBanner /> : null}
        </div>
    );
};
